import { useEffect, useState } from 'react'
import { Description, Field, Label, Switch } from '@headlessui/react'
import LoadingSpinner from '../icons/LoadingSpinner.tsx'
import {
  useClubAdminDeleteDigestMutation,
  useClubAdminGetDigestQuery,
  useClubAdminSendDigestNowMutation,
  useClubAdminUpsertDigestMutation,
} from '../../features/SpeechAgendaApiSlice.ts'
import { DigestConfig } from '../../generated'
import { getApiErrorCode, getApiErrorMessage } from '../../utils/ApiError.ts'
import SimpleModalDialog from '../modals/SimpleModalDialog.tsx'
import { nothingToSendReason } from './nothingToSendReason.ts'
import {
  daysBeforeLabel,
  formatHour,
  hourToSendAtTime,
  sendAtTimeToHour,
} from './digestSendTime.ts'
import ConfirmActionDialog from '../modals/ConfirmActionDialog.tsx'

interface DigestSettingsProps {
  clubId: string
}

interface DigestFormState {
  enabled: boolean
  daysBefore: number
  sendAtTime: number
  skipEnabled: boolean
  skipEveryN: number
}

const DAYS_BEFORE_OPTIONS = [1, 2, 3]

const DEFAULT_SEND_AT_TIME = 9
const DEFAULT_SKIP_EVERY_N = 4

const emptyForm: DigestFormState = {
  enabled: false,
  daysBefore: 1,
  sendAtTime: DEFAULT_SEND_AT_TIME,
  skipEnabled: false,
  skipEveryN: DEFAULT_SKIP_EVERY_N,
}

const toFormState = (config: DigestConfig): DigestFormState => ({
  enabled: config.enabled,
  daysBefore: DAYS_BEFORE_OPTIONS.includes(config.daysBefore)
    ? config.daysBefore
    : 1,
  sendAtTime: sendAtTimeToHour(config.sendAtTime),
  skipEnabled: (config.skipEveryN ?? 0) >= 2,
  skipEveryN: (config.skipEveryN ?? 0) >= 2 ? config.skipEveryN! : DEFAULT_SKIP_EVERY_N,
})

// Read the HTTP status off an RTK Query error when present.
const getHttpStatus = (error: unknown): number | undefined => {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: unknown }).status
    if (typeof status === 'number') return status
  }
  return undefined
}

// "1" → "1st", "2" → "2nd", "4" → "4th" for the skip preview.
const ordinal = (n: number): string => {
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`
  switch (n % 10) {
    case 1:
      return `${n}st`
    case 2:
      return `${n}nd`
    case 3:
      return `${n}rd`
    default:
      return `${n}th`
  }
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => hour)

const labelClassName = 'block text-sm font-medium text-gray-900'
const selectClassName =
  'mt-1 block w-full rounded-md border-0 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600'

const DigestSettings = ({ clubId }: DigestSettingsProps) => {
  const skip = !clubId

  const {
    currentData: digest,
    isLoading,
    isFetching,
    isError,
    error: loadError,
  } = useClubAdminGetDigestQuery(clubId, { skip })

  // A 404 just means the club hasn't configured a digest yet — that's a valid
  // starting state, so only surface genuine load failures.
  const loadStatus = getHttpStatus(loadError)
  const isUnconfigured = isError && loadStatus === 404
  const isGenuineLoadError = isError && !isUnconfigured

  const [upsertDigest, { isLoading: isSaving }] =
    useClubAdminUpsertDigestMutation()
  const [deleteDigest, { isLoading: isDeleting }] =
    useClubAdminDeleteDigestMutation()
  const [sendDigestNow, { isLoading: isSending }] =
    useClubAdminSendDigestNowMutation()

  const [formState, setFormState] = useState<DigestFormState>(emptyForm)
  const [formError, setFormError] = useState<string | null>(null)
  const [confirmingRemove, setConfirmingRemove] = useState(false)
  const [confirmingSend, setConfirmingSend] = useState(false)

  const [modalState, setModalState] = useState({
    isOpen: false,
    success: true,
    title: '',
    description: '',
  })

  // This component stays mounted while `clubId` changes (e.g. navigating
  // between cached admin pages with the digest tab open). Clear the form the
  // moment the club changes so the previous club's settings can't be shown (or
  // saved onto the new club) before the new data resolves. `currentData` above
  // guards the hydrate effect against the previous arg's retained result, but
  // this reset also covers the gap before that value settles. Close any open
  // remove dialog too — otherwise its confirm would delete the newly selected
  // club's digest.
  useEffect(() => {
    setFormState(emptyForm)
    setFormError(null)
    setConfirmingRemove(false)
    setConfirmingSend(false)
  }, [clubId])

  // Keep the form in sync with the query result once it settles: reset to
  // defaults when the club is unconfigured (404), otherwise hydrate from the
  // loaded config. The 404 branch is checked first: after a delete (or another
  // admin removing the digest) the refetch 404s while RTK still retains the
  // last successful `currentData`, so hydrating on `digest` first would restore
  // the just-removed config instead of the empty defaults. `currentData` is
  // also scoped to the active `clubId`, so it never carries the previous club's
  // retained result into the form during a switch.
  useEffect(() => {
    if (isUnconfigured) {
      setFormState(emptyForm)
    } else if (digest) {
      setFormState(toFormState(digest))
    }
  }, [digest, isUnconfigured])

  const setField = <K extends keyof DigestFormState>(
    field: K,
    value: DigestFormState[K]
  ) => {
    setFormState((previous) => ({ ...previous, [field]: value }))
  }

  const openModal = ({
    success,
    title,
    description,
  }: {
    success: boolean
    title: string
    description: string
  }) => {
    setModalState({ isOpen: true, success, title, description })
  }

  const closeModal = () =>
    setModalState((previous) => ({ ...previous, isOpen: false }))

  const resolveSaveErrorMessage = (error: unknown): string => {
    const status = getHttpStatus(error)
    if (status === 401 || status === 403) {
      return "You don't have permission to manage this club's digest."
    }
    switch (getApiErrorCode(error)) {
      case 'VALIDATION_ERROR':
        return 'Please check the fields and try again.'
      default:
        return getApiErrorMessage(
          error,
          'Unable to save digest settings. Try again later'
        )
    }
  }

  const handleSave = async () => {
    if (!clubId) {
      setFormError('Club context is missing. Please reload the page and try again')
      return
    }

    if (
      formState.skipEnabled &&
      (!Number.isInteger(formState.skipEveryN) || formState.skipEveryN < 2)
    ) {
      setFormError('Skip must be a whole number of 2 or more meetings, or turn it off.')
      return
    }

    if (formState.sendAtTime < 0 || formState.sendAtTime > 23) {
      setFormError('Pick a valid send time.')
      return
    }

    setFormError(null)

    const body = {
      enabled: formState.enabled,
      daysBefore: formState.daysBefore,
      sendAtTime: hourToSendAtTime(formState.sendAtTime),
      skipEveryN: formState.skipEnabled ? formState.skipEveryN : undefined,
    }

    try {
      await upsertDigest({ clubId, body }).unwrap()
      openModal({
        success: true,
        title: 'Digest saved',
        description: 'Your meeting digest settings have been saved.',
      })
    } catch (error) {
      console.error('Failed to save digest settings', error)
      setFormError(resolveSaveErrorMessage(error))
    }
  }

  const confirmRemove = async () => {
    setConfirmingRemove(false)
    if (!clubId) return

    try {
      await deleteDigest(clubId).unwrap()
      setFormState(emptyForm)
      openModal({
        success: true,
        title: 'Digest removed',
        description: 'The meeting digest has been removed for this club.',
      })
    } catch (error) {
      console.error('Failed to remove digest', error)
      const status = getHttpStatus(error)
      openModal({
        success: false,
        title: 'Removal failed',
        description:
          status === 401 || status === 403
            ? "You don't have permission to manage this club's digest."
            : getApiErrorMessage(
                error,
                'Unable to remove the digest. Try again later'
              ),
      })
    }
  }

  const handleSendNow = async () => {
    setConfirmingSend(false)
    if (!clubId) return

    try {
      const result = await sendDigestNow(clubId).unwrap()
      if (result.digestsSent >= 1) {
        openModal({
          success: true,
          title: 'Digest sent',
          description:
            'The meeting digest has been emailed to subscribed members.',
        })
      } else {
        openModal({
          success: true,
          title: 'Nothing to send',
          description: nothingToSendReason(result),
        })
      }
    } catch (error) {
      console.error('Failed to send digest', error)
      const status = getHttpStatus(error)
      let description: string
      if (status === 404) {
        description = 'Configure and save a digest before sending.'
      } else if (status === 401 || status === 403) {
        description = "You don't have permission to send this club's digest."
      } else {
        description = getApiErrorMessage(
          error,
          'Unable to send the digest. Try again later'
        )
      }
      openModal({
        success: false,
        title: 'Send failed',
        description,
      })
    }
  }

  if (isLoading) {
    return (
      <div className="py-6 flex justify-center">
        <LoadingSpinner message="Loading digest settings..." />
      </div>
    )
  }

  if (isGenuineLoadError) {
    return (
      <p className="text-sm text-red-600">
        Unable to load digest settings right now. Please refresh and try again
      </p>
    )
  }

  // `isFetching` (not just `isLoading`) covers club-switch refetches where
  // `digest` still holds the previous club's data; block saves until the new
  // club's digest resolves so stale values can't be written to it.
  const isBusy = isSaving || isDeleting || isSending || isFetching

  return (
    <section className="max-w-2xl">
      <header className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Meeting digest</h3>
        <p className="mt-1 text-sm text-gray-600">
          Email members ahead of each meeting so open roles get filled
        </p>
        {isUnconfigured && (
          <p className="mt-2 text-sm text-amber-600">
            No digest configured yet — set your preferences and save to turn it
            on
          </p>
        )}
      </header>

      <div className="space-y-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        {/* Enabled toggle */}
        <Field className="flex items-start gap-3">
          <Switch
            checked={formState.enabled}
            onChange={(checked: boolean) => setField('enabled', checked)}
            className={`${
              formState.enabled ? 'bg-indigo-600' : 'bg-gray-200'
            } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600`}>
            <span
              aria-hidden="true"
              className={`${
                formState.enabled ? 'translate-x-5' : 'translate-x-0'
              } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
            />
          </Switch>
          <div>
            <Label
              as="span"
              className="text-sm font-medium text-gray-900"
              passive>
              Enable meeting digest
            </Label>
            <Description as="span" className="block text-xs text-gray-500">
              When off, no digest emails are sent even if the settings below are configured
            </Description>
          </div>
        </Field>

        {/* Timing */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="digest-days-before" className={labelClassName}>
              Send this many days before
            </label>
            <select
              id="digest-days-before"
              value={formState.daysBefore}
              onChange={(event) =>
                setField('daysBefore', Number(event.target.value))
              }
              disabled={!formState.enabled}
              className={`${selectClassName} disabled:cursor-not-allowed disabled:opacity-60`}>
              {DAYS_BEFORE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {daysBeforeLabel(option)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="digest-send-time" className={labelClassName}>
              Send time
            </label>
            <select
              id="digest-send-time"
              value={formState.sendAtTime}
              onChange={(event) =>
                setField('sendAtTime', Number(event.target.value))
              }
              disabled={!formState.enabled}
              className={`${selectClassName} disabled:cursor-not-allowed disabled:opacity-60`}>
              {HOUR_OPTIONS.map((hour) => (
                <option key={hour} value={hour}>
                  {formatHour(hour)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Uses the club&apos;s local time
            </p>
          </div>
        </div>

        {/* Recurring skip */}
        <div>
          <Field className="flex items-start gap-3">
            <Switch
              checked={formState.skipEnabled}
              onChange={(checked: boolean) => setField('skipEnabled', checked)}
              className={`${
                formState.skipEnabled ? 'bg-indigo-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600`}>
              <span
                aria-hidden="true"
                className={`${
                  formState.skipEnabled ? 'translate-x-5' : 'translate-x-0'
                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </Switch>
            <div>
              <Label
                as="span"
                className="text-sm font-medium text-gray-900"
                passive>
                Skip recurring meetings
              </Label>
              <Description as="span" className="block text-xs text-gray-500">
                Skip the digest for every Nth meeting in the series.
              </Description>
            </div>
          </Field>

          {formState.skipEnabled && (
            <div className="mt-4 pl-14">
              <label htmlFor="digest-skip-every-n" className={labelClassName}>
                Skip every
              </label>
              <input
                id="digest-skip-every-n"
                type="number"
                min={2}
                step={1}
                value={formState.skipEveryN}
                onChange={(event) =>
                  setField('skipEveryN', Number(event.target.value))
                }
                className={`${selectClassName} sm:w-32`}
              />
              <p className="mt-1 text-xs text-gray-500">
                {Number.isInteger(formState.skipEveryN) &&
                formState.skipEveryN >= 2
                  ? `Skips every ${ordinal(formState.skipEveryN)} meeting.`
                  : 'Enter a whole number of 2 or more.'}
              </p>
            </div>
          )}
        </div>

        {formError && <p className="text-sm text-red-600">{formError}</p>}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-6">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={isBusy}
            className="rounded-md bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60">
            {isSaving ? 'Saving…' : 'Save digest settings'}
          </button>

          <div className="flex flex-wrap items-center gap-3">
            {!isUnconfigured && (
              <button
                type="button"
                onClick={() => setConfirmingSend(true)}
                disabled={isBusy}
                className="rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-indigo-600 shadow-sm ring-1 ring-inset ring-indigo-300 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60">
                {isSending ? 'Sending…' : 'Send now'}
              </button>
            )}

            {!isUnconfigured && (
              <button
                type="button"
                onClick={() => setConfirmingRemove(true)}
                disabled={isBusy}
                className="rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-red-300 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60">
                {isDeleting ? 'Removing…' : 'Remove digest'}
              </button>
            )}
          </div>
        </div>
      </div>

      <SimpleModalDialog
        isOpen={modalState.isOpen}
        onClose={closeModal}
        success={modalState.success}
        title={modalState.title}
        description={modalState.description}
        buttonText="Close"
      />

      <ConfirmActionDialog
        isOpen={confirmingRemove}
        title="Remove digest?"
        description="This deletes the club's digest configuration and stops all digest emails. You can set it up again later."
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={() => void confirmRemove()}
        onCancel={() => setConfirmingRemove(false)}
      />

      <ConfirmActionDialog
        isOpen={confirmingSend}
        variant="default"
        title="Send digest now?"
        description="This immediately emails the meeting digest for the next upcoming meeting to all subscribed members, bypassing the schedule."
        confirmText="Send now"
        cancelText="Cancel"
        onConfirm={() => void handleSendNow()}
        onCancel={() => setConfirmingSend(false)}
      />
    </section>
  )
}

export default DigestSettings
